function getEnvironmentVariable(name: string) {
  return typeof process !== "undefined" && process.env?.[name]
    ? process.env[name]
    : import.meta.env[name];
}

function getRuntimeAppEnv(): string | undefined {
  return typeof window !== "undefined" ? window.__APP_ENV__ : undefined;
}

function isAppEnv(value: string | undefined): value is AppEnv {
  return (
    value === "development" ||
    value === "stage" ||
    value === "sandbox" ||
    value === "production"
  );
}

function getAppEnv(): AppEnv {
  const appEnv = getRuntimeAppEnv() ?? getEnvironmentVariable("VITE_APP_ENV");

  return isAppEnv(appEnv) ? appEnv : "development";
}

export const BROWSER: string | undefined = getEnvironmentVariable("BROWSER");

export const PORT: number = Number(getEnvironmentVariable("PORT") ?? 8080);

function isWxtExtensionUi(): boolean {
  return (
    typeof BROWSER === "string" &&
    typeof chrome !== "undefined" &&
    Boolean(chrome.runtime?.id)
  );
}

/** True for extension pages, or the WXT content-script UI on an allowed host tab. */
export const IS_CHROME_EXTENSION =
  (typeof window !== "undefined" &&
    window.location.protocol === "chrome-extension:") ||
  isWxtExtensionUi();

/** True when running inside a Capacitor-wrapped native shell (mobile spike). */
export const IS_CAPACITOR: boolean =
  typeof window !== "undefined" &&
  Boolean(
    (
      window as unknown as {
        Capacitor?: { isNativePlatform?: () => boolean };
      }
    ).Capacitor?.isNativePlatform?.(),
  );

/**
 * Custom URL scheme the mobile wrapper registers so the system browser can
 * hand control back to the app after Okta login (mobile spike). Must match
 * the trustedOrigins entry in apps/server/src/shared/utils/settings.ts, the
 * MOBILE_CALLBACK_SCHEME_PREFIX in mobile-oauth-handoff.ts, and the
 * CFBundleURLSchemes entry in apps/mobile/ios/App/App/Info.plist.
 */
export const MOBILE_OAUTH_CALLBACK_URL =
  "com.elationhealth.stitchweb://oauth-callback";

export const IS_TEST = process.env.NODE_ENV === "test";

export const IS_AUTOMATION =
  typeof navigator !== "undefined" && navigator.webdriver;

/**
 * True when this document is rendered inside a parent frame that is *not*
 * the chrome extension's own content-script-injected overlay.
 *
 * The chrome extension (WXT) renders the React app in a content-script shadow
 * root on the host tab, or historically in an iframe with a chrome-extension
 * URL, so `window.parent !== window` can be true. The rest of the app uses
 * `IS_IN_IFRAME` to gate the Stitch-in-Elation iframe auth + transcription
 * handshakes, which the chrome extension does not participate in. Excluding
 * `IS_CHROME_EXTENSION` keeps those two iframe modes from cross-wiring.
 */
export const IS_IN_IFRAME = window.parent !== window && !IS_CHROME_EXTENSION;

function getServiceUrl(useLocalhostServer: boolean = false): string {
  if (IS_CHROME_EXTENSION) {
    // An explicitly baked URL wins over inferring one from the host page. The
    // inference below maps every dev host to the shared dev deployment, which
    // the e2e build needs to override so the panel talks to the PR's own
    // server. Released builds don't set it and keep inferring.
    const bakedServiceUrl = getEnvironmentVariable("VITE_SERVICE_URL");

    if (bakedServiceUrl) {
      return bakedServiceUrl;
    }

    const { hostname } = window.location;

    if (hostname.endsWith(".el8-dev-services.internal")) {
      return "https://stitch-web-dev.el8-dev-services.internal";
    }

    if (hostname.endsWith(".el8-stage-services.internal")) {
      return "https://stitch-web-stage.el8-stage-services.internal";
    }

    if (hostname.endsWith(".el8-sandbox-services.internal")) {
      return "https://stitch-web-sandbox.el8-sandbox-services.internal";
    }

    if (hostname === "localhost") {
      return "http://localhost:8880";
    }

    return "https://slate.elationemr.com";
  }

  if (IS_TEST) {
    return window.location.origin;
  }

  if (useLocalhostServer) {
    return `http://localhost:${PORT}`;
  }

  const serviceUrl = getEnvironmentVariable("VITE_SERVICE_URL");

  if (serviceUrl) {
    return serviceUrl;
  }

  // Capacitor's WebView origin (capacitor://localhost / https://localhost) is
  // not the API. Prefer baking VITE_SERVICE_URL at build time; fall back to the
  // iOS Simulator → host loopback default used by local server docs.
  if (IS_CAPACITOR) {
    return "http://localhost:8880";
  }

  if (getEnvironmentVariable("STORYBOOK") === "true") {
    return "http://localhost:6009";
  }

  return window.location.origin;
}

export const DEV_LOGIN_EMAIL: string | undefined = getEnvironmentVariable(
  "VITE_DEV_LOGIN_EMAIL",
);

export const DEV_LOGIN_PASSWORD: string | undefined = getEnvironmentVariable(
  "VITE_DEV_LOGIN_PASSWORD",
);

export const MOCKING_ENABLED =
  getEnvironmentVariable("VITE_MOCKING_ENABLED") === "true";

export const APP_VERSION: string =
  getEnvironmentVariable("VITE_APP_VERSION") ?? "0.0.0-development";

export const APP_ENV: AppEnv = getAppEnv();

export const CDN_ASSET_URL: string | undefined =
  getEnvironmentVariable("VITE_CDN_ASSET_URL");

export const SERVICE_URL: string = getServiceUrl();

export const DATADOG_CLIENT_TOKEN: string | undefined = getEnvironmentVariable(
  "VITE_DATADOG_CLIENT_TOKEN",
);

export const DATADOG_SITE: string =
  getEnvironmentVariable("VITE_DATADOG_SITE") ?? "datadoghq.com";

export const DATADOG_SERVICE: string =
  getEnvironmentVariable("VITE_DATADOG_SERVICE") ?? "stitch-web";

export const DATADOG_RUM_APPLICATION_ID: string | undefined =
  getEnvironmentVariable("VITE_DATADOG_RUM_APPLICATION_ID");

export const IS_PRODUCTION = APP_ENV === "production";

export const OTEL_CLIENT_KEY: string | undefined = getEnvironmentVariable(
  "VITE_OTEL_CLIENT_KEY",
);

export const OTEL_COLLECTOR_URL: string = IS_PRODUCTION
  ? "https://otel-ingest.el8-production-services.el8.io"
  : "https://public-otel-collector-dev.el8-dev-services.internal";

function getStitchApiUrl(): string {
  if (MOCKING_ENABLED) {
    return window.location.origin + "/gw/stitch-api";
  }

  const viteStitchApiUrl = getEnvironmentVariable("VITE_STITCH_API_URL");

  if (viteStitchApiUrl) {
    return viteStitchApiUrl;
  }

  switch (APP_ENV) {
    case "development":
      return "https://stitch-api-dev.el8-dev-services.internal/";
    case "stage":
      return "https://stitch-api-stage.el8-stage-services.internal/";
    case "sandbox":
      return "https://stitch-api-sandbox.el8-sandbox-services.internal/";
    case "production":
      return "https://stitch-api-production.el8-production-services.internal/";
  }
}

export const STITCH_API_URL: string = getStitchApiUrl();

export const OKTA_SCOPES: string[] = [
  "openid",
  "profile",
  "offline_access",
  "email",
  "stitch",
];

const environment = {
  DEV_LOGIN_EMAIL,
  DEV_LOGIN_PASSWORD,
  MOCKING_ENABLED,
  APP_VERSION,
  PORT,
  CDN_ASSET_URL,
  SERVICE_URL,
  IS_CHROME_EXTENSION,
  IS_CAPACITOR,
  OKTA_SCOPES,
  STITCH_API_URL,
  DATADOG_CLIENT_TOKEN,
  DATADOG_SITE,
  DATADOG_SERVICE,
  DATADOG_RUM_APPLICATION_ID,
};

export default environment;
